import { useEffect, useState } from "react";
import {
  Fuel,
  Coffee,
  Landmark,
  Trees,
  Sparkles,
  Navigation,
  ExternalLink,
  LoaderCircle,
  MapPin,
  RefreshCw,
  Clock3,
  ParkingCircle,
  ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api } from "./lib/api";
import type { LatLng, NearbyPlace, Place, PlaceCategory } from "./lib/types";
import { distanceMeters, formatDistance } from "./lib/navigation";

// Editorial ideas researched against the venue's official pages on 2026-09-30.
// Coordinates and identities were resolved through the live OSM/Photon search.
const ideas = [
  {
    place: {
      id: "N:951616833",
      name: "מוז״א · מוזיאון ארץ ישראל",
      address: "חיים לבנון 2, תל אביב–יפו",
      lat: 32.1034077,
      lng: 34.7963224,
    },
    category: "culture",
    description: "אוספים, צילום, ארכאולוגיה ובוסתן פתוח בתוך העיר.",
    source: "https://www.eretzmuseum.org.il/על-המוזיאון/",
  },
  {
    place: {
      id: "W:247532189",
      name: "גן לאומי ירקון · תל אפק",
      address: "מתחם אנטיפטריס, מועצה אזורית דרום השרון",
      lat: 32.1051135,
      lng: 34.9299653,
    },
    category: "nature",
    description:
      "רעיון ליציאה אל טבע והיסטוריה. בדקו מראש שעות, הרשמה וכניסה באתר הרשמי.",
    source: "https://www.parks.org.il/reserve-park/yarkon-3/",
  },
];

const categories: { id: PlaceCategory; label: string; icon: LucideIcon }[] = [
  { id: "parking", label: "חניה", icon: ParkingCircle },
  { id: "shelter", label: "מקלט", icon: ShieldCheck },
  { id: "fuel", label: "דלק", icon: Fuel },
  { id: "food", label: "אוכל וקפה", icon: Coffee },
  { id: "culture", label: "תרבות", icon: Landmark },
  { id: "nature", label: "טבע", icon: Trees },
  { id: "attractions", label: "לגלות", icon: Sparkles },
];
const kinds: Record<string, string> = {
  parking: "חניון",
  bomb_shelter: "מקלט",
  fuel: "תחנת דלק",
  restaurant: "מסעדה",
  cafe: "בית קפה",
  bar: "בר",
  pub: "פאב",
  biergarten: "גן בירה",
  fast_food: "אוכל מהיר",
  food_court: "מתחם אוכל",
  museum: "מוזיאון",
  gallery: "גלריה",
  theatre: "תיאטרון",
  cinema: "קולנוע",
  arts_centre: "מרכז אמנויות",
  park: "פארק",
  garden: "גן",
  nature_reserve: "שמורת טבע",
  viewpoint: "תצפית",
  attraction: "נקודת עניין",
  zoo: "גן חיות",
  aquarium: "אקווריום",
  theme_park: "פארק שעשועים",
  beach: "חוף",
  picnic_site: "אזור פיקניק",
  castle: "אתר היסטורי",
  archaeological_site: "אתר ארכאולוגי",
};
function webUrl(value?: string | null): string | undefined {
  if (!value) return;
  try {
    const url = new URL(value);
    if (["https:", "http:"].includes(url.protocol)) return url.href;
  } catch {
    /* Untrusted OSM URLs are optional. */
  }
}

export default function Discovery({
  center,
  initialCategory,
  onSelect,
  onPlacesChange,
  onSuggest,
  centerLabel,
}: {
  onSuggest?: () => void;
  center: LatLng;
  centerLabel?: string;
  initialCategory: PlaceCategory;
  onSelect: (place: Place) => void;
  onPlacesChange: (places: NearbyPlace[]) => void;
}) {
  const [category, setCategory] = useState(initialCategory);
  const [places, setPlaces] = useState<NearbyPlace[]>([]);
  const [busy, setBusy] = useState(true),
    [error, setError] = useState(""),
    [retry, setRetry] = useState(0);
  const [sourceInfo, setSourceInfo] = useState(""), [warning, setWarning] = useState("");
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const localIdeas = ideas.filter(
    (idea) =>
      (category === "attractions" || category === idea.category) &&
      distanceMeters(center, idea.place) < 40000,
  );
  useEffect(() => {
    const controller = new AbortController();
    setBusy(true); setSourceInfo(""); setWarning(""); setUpdatedAt(null);
    setError("");
    setPlaces([]);
    onPlacesChange([]);
    api
      .nearby(center, category, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setSourceInfo(result.attribution || result.provider); setWarning(result.warning || "");
        setUpdatedAt(result.updatedAt);
        setPlaces(result.places);
        onPlacesChange(result.places);
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setError(
            cause instanceof Error
              ? cause.message
              : "לא הצלחנו לחפש מקומות כרגע",
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setBusy(false);
      });
    return () => controller.abort();
  }, [center.lat, center.lng, category, retry, onPlacesChange]);
  return (
    <section className="discovery">
      <h2>מקומות בסביבה</h2>
      <p className="discovery-intro">
        עד 5 ק״מ · {centerLabel || "סביב הנקודה שנבחרה"} · הקרובים תחילה לפי מרחק אווירי.
      </p>
      <div className="discovery-tabs" role="group" aria-label="סוג מקום">
        {categories.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={category === id ? "selected" : ""}
            aria-pressed={category === id}
            onClick={() => setCategory(id)}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </div>
      {warning && <p role="status" className="discovery-warning">{warning}</p>}
      <div className="discovery-results" aria-live="polite" aria-busy={busy}>
        {localIdeas.length > 0 && (
          <div className="discovery-ideas">
            <span className="eyebrow">עוד באזור · עד 40 ק״מ</span>
            {localIdeas.map((idea) => (
              <article key={idea.place.id}>
                <div>
                  <h3>{idea.place.name}</h3>
                  <p>{idea.description}</p>
                  <a
                    href={idea.source}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    האתר הרשמי <ExternalLink size={11} />
                  </a>
                </div>
                <button
                  className="discovery-go"
                  onClick={() => onSelect(idea.place)}
                  aria-label={`תכנון מסלול אל ${idea.place.name}`}
                >
                  <Navigation size={17} />
                  <span>לשם</span>
                </button>
              </article>
            ))}
          </div>
        )}
        {busy ? (
          <div className="discovery-empty">
            <LoaderCircle className="spin" />
            <strong>מחפשים מקומות סביבך</strong>
            <span>החיפוש הראשון באזור עשוי לקחת כמה שניות</span>
          </div>
        ) : error ? (
          <div className="discovery-empty">
            <MapPin />
            <strong>החיפוש לא הושלם</strong>
            <p>{error}</p>
            <button
              className="outline-btn"
              onClick={() => setRetry((v) => v + 1)}
            >
              <RefreshCw size={16} />
              לנסות שוב
            </button>
          </div>
        ) : !places.length ? (
          <div className="discovery-empty">
            <MapPin />
            <strong>{category === "shelter" ? "לא נמצאו מקלטים במאגר באזור זה" : "לא נמצאו מקומות במאגר באזור זה"}</strong>
            <span>אפשר לבחור סוג אחר או להזיז את נקודת החיפוש במפה.</span>
          </div>
        ) : (
          places.map((place) => {
            const Icon =
              categories.find((c) => c.id === place.category)?.icon || MapPin;
            const website = webUrl(place.website);
            const description = [
              kinds[place.kind],
              place.brand && place.brand !== place.name ? place.brand : undefined,
            ].filter(Boolean).join(" · ");
            return (
              <article className="discovery-place" key={place.id}>
                <div className={"discovery-place-icon " + place.category}>
                  <Icon size={21} />
                </div>
                <div className="discovery-place-body">
                  <h3>{place.name}</h3>
                  {description && <p>{description}</p>}
                  <span className="discovery-distance">
                    <MapPin size={12} />
                    {formatDistance(place.distanceMeters)} בקו אווירי
                  </span>
                  {place.address && <small>{place.address}</small>}
                  {place.openingHours && category !== "shelter" && (
                    <small className="place-hours">
                      <Clock3 size={12} />
                      {place.openingHours === "24/7" ? (
                        "פתוח 24 שעות"
                      ) : (
                        <bdi>{place.openingHours}</bdi>
                      )}
                    </small>
                  )}
                  <div className="discovery-links">
                    {webUrl(place.sourceUrl) && <a href={webUrl(place.sourceUrl)} target="_blank" rel="noopener noreferrer">מקור במפה <ExternalLink size={12}/></a>}
                    {website && (
                      <a
                        href={website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        אתר המקום <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>
                <button
                  className="discovery-go"
                  onClick={() => onSelect(place)}
                  aria-label={`תכנון מסלול אל ${place.name}`}
                >
                  <Navigation size={17} />
                  <span>{category === "shelter" ? "מסלול הליכה" : "לשם"}</span>
                </button>
              </article>
            );
          })
        )}
      </div>
      {onSuggest && <button className="outline-btn" onClick={onSuggest}><MapPin size={18}/>חסר מקום? מציעים נקודה במפה</button>}
      {updatedAt && (category === "shelter" || category === "parking") && <p className="discovery-intro">נתוני המפה מתאריך {new Date(updatedAt).toLocaleDateString("he-IL")}</p>}
      <details className="discovery-source">
        <summary>מידע על המפה</summary>
        <p>{sourceInfo || "מאגרי מפות פתוחים ומקומות שאושרו ב־Desty"}</p>
        <p>פרטי המקום ושעות הפתיחה עשויים להשתנות; כדאי לבדוק באתר המקום.</p>
      </details>
    </section>
  );
}
